import { useMemo, useState } from 'react';
import { payments as Api, type Payment, type PaymentStats } from '@tvx/api-client';
import { useAsync } from '../lib/useAsync';
import { SkeletonRows } from '../components/Shimmer';
import { useSelection, TickAll, Tick, BulkActions } from '../components/Bulk';
import { PaymentForm } from './PaymentForm';
import { stampDay } from '../lib/time';

const inr = (n: number) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const day = stampDay;

const STATUS_COLOUR: Record<Payment['status'], string> = {
  paid: 'rgba(62,207,142,.14)',
  pending: 'rgba(var(--goldrgb), .14)',
  failed: 'rgba(240,100,91,.13)',
  refunded: 'rgba(var(--wash), calc(.06 * var(--washx)))',
};

const FILTERS = ['all', 'paid', 'pending', 'failed', 'refunded'] as const;

export function Payments() {
  const { data, error, loading, reload } = useAsync((signal) => Api.list(signal));
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>('all');
  const [editing, setEditing] = useState<Payment | 'new' | null>(null);

  const rows = useMemo(() => {
    const all = data?.payments ?? [];
    return filter === 'all' ? all : all.filter((p) => p.status === filter);
  }, [data, filter]);

  function exportLedger() {
    const head = ['Invoice', 'Date', 'Member', 'Item', 'Amount', 'GST', 'Method', 'Status', 'Reference'];
    const cell = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csv = [head, ...(data?.payments ?? []).map((p) => [
      p.invoice_no, p.paid_at ?? p.created_at, p.member_name ?? '', p.item,
      p.amount, p.gst, p.method, p.status, p.reference,
    ])].map((r) => r.map(cell).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = 'tradevix-ledger.csv'; a.click();
    URL.revokeObjectURL(url);
  }

  const sel = useSelection(rows);

  return (
    <div className="pg">
      <div className="rowbar">
        <div>
          <h1 style={{ fontSize: 29 }}>Payments &amp; invoices</h1>
          <p style={{ fontSize: 13.5, color: 'var(--tx2)', marginTop: 7 }}>
            Recorded by hand — no gateway is connected yet.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap' }}>
          <button className="b bo" type="button" onClick={exportLedger} disabled={!data?.payments.length}>Export ledger</button>
          <button className="b bg" type="button" onClick={() => setEditing('new')}>Record a payment</button>
        </div>
      </div>

      {error ? <div className="err">Could not load payments — {error.message}</div> : null}

      {data ? <Totals stats={data.stats} /> : null}

      <div className="crd" style={{ padding: '20px 22px' }}>
        <div className="sg" style={{ marginBottom: 16 }}>
          {FILTERS.map((f) => (
            <span key={f} {...(filter === f ? { 'data-on': '' } : {})} onClick={() => setFilter(f)}>
              {f === 'all' ? 'All' : f[0]!.toUpperCase() + f.slice(1)}
            </span>
          ))}
        </div>

        <BulkActions
          path="/payments" noun="payment"
          rows={sel.picked.map((p) => ({ id: p.id, label: String(p.invoice_no) }))}
          actions={[]}
          onDone={async () => { sel.clear(); await reload(); }}
        />

        <div className="tblx">
          <table className="tbl">
            <thead>
              <tr>
                <TickAll on={sel.allOn} some={sel.picked.length > 0} onChange={sel.toggleAll} />
                <th>Invoice</th><th>Member</th><th>Item</th><th>Amount</th>
                <th>Method</th><th>Status</th><th>Date</th><th>Source</th><th />
              </tr>
            </thead>
            <tbody>
              {loading && !data ? (
                <SkeletonRows cols={10} tick />
              ) : rows.length === 0 ? (
                <tr><td colSpan={10} className="muted" style={{ padding: '26px 8px' }}>
                  {data?.payments.length ? 'Nothing with that status.'
                    : 'No payments recorded yet. Every payment taken outside the site is entered here.'}
                </td></tr>
              ) : rows.map((p) => (
                <tr key={p.id} style={sel.isOn(p.id) ? { background: 'rgba(var(--goldrgb), .05)' } : undefined}>
                  <Tick on={sel.isOn(p.id)} label={String(p.invoice_no)}
                    onChange={(v) => sel.toggle(p.id, v)} />
                  <td style={{ fontWeight: 700 }}>{p.tax_invoice_no || p.invoice_no || '—'}</td>
                  <td>
                    {p.member_name ?? <span className="muted">Not linked</span>}
                    {p.member_email ? <div style={{ fontSize: 11.5, color: 'var(--tx3)' }}>{p.member_email}</div> : null}
                  </td>
                  <td style={{ color: 'var(--tx2)' }}>{p.item || '—'}</td>
                  <td>
                    {inr(p.amount)}
                    {p.gst ? <div style={{ fontSize: 11, color: 'var(--tx3)' }}>incl. {inr(p.gst)} GST</div> : null}
                  </td>
                  <td className="muted">{p.method}</td>
                  <td><span className="tg" style={{ background: STATUS_COLOUR[p.status] }}>{p.status}</span></td>
                  <td className="muted">{day(p.paid_at ?? p.created_at)}</td>
                  <td className="muted" style={{ fontSize: 12 }}>
                    {p.source === 'enrol' ? 'Added from member' : '—'}
                  </td>
                  <td className="right">
                    {p.status === 'paid' ? (
                      <a className="b bo" style={{ padding: '6px 12px', fontSize: 11.5, marginRight: 6, textDecoration: 'none' }}
                        href={`/api/payments/${p.id}/invoice.pdf`} download>PDF</a>
                    ) : null}
                    <button className="b bo" style={{ padding: '6px 12px', fontSize: 11.5 }}
                      type="button" onClick={() => setEditing(p)}>Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing ? (
        <PaymentForm
          payment={editing === 'new' ? null : editing}
          members={data?.members ?? []}
          nextInvoice={data?.nextInvoice ?? ''}
          onCancel={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await reload(); }}
        />
      ) : null}
    </div>
  );
}

function Totals({ stats }: { stats: PaymentStats }) {
  const nothing = stats.month.n === 0 && stats.failed.n === 0 && stats.refunded.n === 0;
  if (nothing) return null;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 16, marginBottom: 18 }}>
      <div className="crd" style={{ padding: '20px 22px' }}>
        <div className="kh">This month</div>
        <div style={{ fontFamily: 'var(--fd)', fontSize: 27, fontWeight: 600, marginTop: 8 }}>{inr(stats.month.gross)}</div>
        <div style={{ fontSize: 11.5, color: 'var(--tx3)', marginTop: 6 }}>
          {stats.month.n} payment{stats.month.n === 1 ? '' : 's'} · {inr(stats.month.gst)} GST
        </div>
      </div>
      <div className="crd" style={{ padding: '20px 22px' }}>
        <div className="kh">Failed</div>
        <div style={{ fontFamily: 'var(--fd)', fontSize: 27, fontWeight: 600, marginTop: 8,
          color: stats.failed.n ? 'var(--down)' : undefined }}>{stats.failed.n}</div>
        <div style={{ fontSize: 11.5, color: 'var(--tx3)', marginTop: 6 }}>{inr(stats.failed.amt)} not collected</div>
      </div>
      <div className="crd" style={{ padding: '20px 22px' }}>
        <div className="kh">Refunded</div>
        <div style={{ fontFamily: 'var(--fd)', fontSize: 27, fontWeight: 600, marginTop: 8 }}>{stats.refunded.n}</div>
        <div style={{ fontSize: 11.5, color: 'var(--tx3)', marginTop: 6 }}>{inr(stats.refunded.amt)} returned</div>
      </div>
    </div>
  );
}
